/**
 * Admin Layout Component
 * Protected layout wrapper for admin-only routes
 * Includes AdminGuard and uses bg-slate-50 for admin dashboard
 */

import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Sidebar } from './Sidebar';
import { AdminGuard } from '../AdminGuard';
import { useAuth } from '../../contexts/AuthContext';
import { AdminAgentDrawer } from '../../features/admin/admin-agent/components/AdminAgentDrawer';
import {
  AdminAgentDrawerProvider,
  useAdminAgentDrawer,
} from '../../features/admin/admin-agent/context/AdminAgentDrawerContext';
import { AdminFeedbackNotifications } from '../../features/admin/feedback/components/AdminFeedbackNotifications';

// ============================================
// Props Interface
// ============================================

interface AdminLayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
}

// ============================================
// Admin Layout Component
// ============================================

const SIDEBAR_WIDTH = { expanded: '16rem', collapsed: '4rem' } as const;
const AdminLayoutBody: React.FC<AdminLayoutProps> = ({ children, header }) => {
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const drawer = useAdminAgentDrawer();

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const sidebarWidth = isSidebarCollapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded;
  const drawerWidth = drawer.open ? `${drawer.width}px` : '0px';

  return (
    <AdminGuard>
      <Box className="min-h-screen overflow-x-hidden bg-slate-50 dark:bg-slate-900">
        {/* Fixed Full-Height Sidebar */}
        {user && (
          <>
            <AdminFeedbackNotifications />
            <AdminAgentDrawer />
            <Sidebar
              user={user}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
            />
          </>
        )}

        {/* Main Content Area - Positioned to the right of sidebar */}
        <Box
          className={`flex min-h-screen min-w-0 flex-col ${
            drawer.isResizing ? '' : 'transition-all duration-300'
          } ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}
          sx={{
            width: `calc(100% - ${sidebarWidth})`,
            // lg and up: the open drawer pushes the content; below it, the drawer overlays.
            '@media (min-width: 1024px)': {
              width: `calc(100% - ${sidebarWidth} - ${drawerWidth})`,
            },
          }}
        >
          {/* Header - Sticky at top of content area */}
          {header}

          {/* Page Content */}
          <Box className="min-w-0 flex-1 overflow-x-hidden">{children}</Box>
        </Box>
      </Box>
    </AdminGuard>
  );
};

export const AdminLayout: React.FC<AdminLayoutProps> = (props) => (
  <AdminAgentDrawerProvider>
    <AdminLayoutBody {...props} />
  </AdminAgentDrawerProvider>
);
